// Copyright (c) Docugami, Inc. All rights reserved.

import { CoachMarkId } from "coachmarks/CoachMarkTypes";
import { ClusterInfo } from "networking/api";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { AppState } from "store";
import { mapVisibleUserClusters } from "store/state_helpers";
import { getAutomationIdForDocSetId } from "utils/Automation";
import { ExplorerButtonId, ExplorerViewType, NavigationGroup } from "../components/myContentExplorer/ExplorerTypes";
import { NavigationListItem } from "../components/myContentExplorer/NavigationItemList";
import { PlaceholderText } from "../components/myContentExplorer/PlaceholderText";
import { IExplorerNavigationGroup } from "./ExplorerNavigationGroups";
import { IExplorerStatusItem, useDocSetGroupStatusItems } from "./ExplorerStatus";
import { MY_CONTENT_PAGE, NavigationType, useNavigationService } from "./navigationService";
import { usePageLimits } from "./PageLimits";

export function useUserDocSetsNavigationGroup() {
    const userDocSets = useSelector((state: AppState) => mapVisibleUserClusters(state.clusters));
    const statusItems = useDocSetGroupStatusItems();
    return useDocSetNavigationGroup(userDocSets, "Doc Sets", statusItems);
}

export function useDocSetNavigationGroup (
    docSets: ClusterInfo[],
    title: string,
    statusItems: IExplorerStatusItem[]): IExplorerNavigationGroup {
    const { pageUploadDisabled } = usePageLimits();
    const navService = useNavigationService();
    const location = useLocation();

    const items = useMemo((): NavigationListItem[] => {
        return docSets.map<NavigationListItem>(docSet => {
            return {
                id: docSet.clusterId!,
                title: docSet.label!,
                type: ExplorerViewType.DocSet,
                onClick: () => navService.navToDocSetPage(NavigationType.PushNewPage, docSet.clusterId!),
                automationId: getAutomationIdForDocSetId(docSet.clusterId!)
            };
        });
    }, [docSets, navService]);

    const getPlaceholder = (): JSX.Element => {
        if (pageUploadDisabled) {
            return <PlaceholderText text={"No doc sets yet... upgrade now to be able to upload some files"} onClick={() => navService.navToAccountPage(NavigationType.PushNewPage)} />
        } else {
            // If we are already on the add docs page, do not show an active link to redirect to it
            const onAddDocsPage = location.pathname?.startsWith(`/${MY_CONTENT_PAGE}/${ExplorerButtonId.AddDocs}`);
            return <PlaceholderText text={"No doc sets yet... upload and assign some files to create your first."} onClick={!onAddDocsPage ? navService.navToAddDocsPage : undefined} />
        }
    }

    return {
        id: NavigationGroup.UserDocSets,
        title: title,
        items: items,
        fallbackId: CoachMarkId.EmptyDocSets,
        placeholder: getPlaceholder(),
        onExpand: items.length === 0 ? () => navService.navToEmptyContentCoachmark(NavigationType.PushNewPage) : undefined,
        statusItems: statusItems
    }
}
